import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useState } from "react"
import { api } from "../../../../convex/_generated/api"
import type { Doc, Id } from "../../../../convex/_generated/dataModel"

export const Route = createFileRoute("/_authed/admin/users")({
  component: AdminUsers,
})

type Role = "super_admin" | "admin" | "host" | "player"
const ALL_ROLES: ReadonlyArray<Role> = ["super_admin", "admin", "host", "player"]
const ADMIN_ASSIGNABLE: ReadonlyArray<Role> = ["host", "player"]

function AdminUsers() {
  const users = useQuery(api.auth.listAll)
  const me = useQuery(api.auth.getCurrentUser)

  const myRole = me?.role as Role | undefined
  const canAssignAnyRole = myRole === "super_admin"

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-foreground)]">
          {canAssignAnyRole
            ? "Super-admin: you can assign any role, including admin and super-admin."
            : "Admin: you can promote users to host or player. Only super-admins can mint new admins."}
        </p>
      </div>

      {users === undefined || me === undefined ? (
        <p className="text-sm text-[color:var(--muted-foreground)]">Loading…</p>
      ) : users.length === 0 ? (
        <p className="text-sm text-[color:var(--muted-foreground)]">
          No users yet.
        </p>
      ) : (
        <ul className="divide-y divide-[color:var(--border)] rounded-md border border-[color:var(--border)]">
          {users.map((user) => (
            <UserRow
              key={user._id}
              user={user}
              isMe={me?._id === user._id}
              myRole={myRole}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function UserRow({
  user,
  isMe,
  myRole,
}: {
  user: Doc<"users">
  isMe: boolean
  myRole: Role | undefined
}) {
  const setUserRole = useMutation(api.auth.setUserRole)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // What roles can the current user assign to THIS user?
  // - super_admin: everything
  // - regular admin: only host/player AND only if target isn't currently
  //   admin/super_admin (can't touch peers/superiors)
  let availableRoles: ReadonlyArray<Role>
  let isLocked = false
  if (myRole === "super_admin") {
    availableRoles = ALL_ROLES
  } else if (myRole === "admin") {
    if (user.role === "super_admin" || user.role === "admin") {
      availableRoles = [user.role as Role] // can't change, just display current
      isLocked = true
    } else {
      availableRoles = ADMIN_ASSIGNABLE
    }
  } else {
    availableRoles = [user.role as Role]
    isLocked = true
  }

  async function handleRoleChange(role: Role) {
    if (role === user.role) return
    setSaving(true)
    setError(null)
    try {
      await setUserRole({ userId: user._id as Id<"users">, role })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const lastSeen = user.lastSeenAt
    ? new Date(user.lastSeenAt).toLocaleString(undefined, {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—"

  return (
    <li className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          {user.displayName ?? user.phoneNumber ?? user.email ?? user._id}
          {isMe && (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
              you
            </span>
          )}
          {user.role === "super_admin" && (
            <span className="ml-2 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900">
              super
            </span>
          )}
        </p>
        <p className="truncate text-xs text-[color:var(--muted-foreground)]">
          {user.email ?? user.phoneNumber ?? "No contact"} · last seen {lastSeen}
        </p>
        {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
      </div>
      <select
        value={user.role}
        onChange={(e) => handleRoleChange(e.target.value as Role)}
        disabled={saving || isLocked}
        title={
          isLocked
            ? "Only super-admins can change this user's role"
            : undefined
        }
        className="rounded-md border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
      >
        {availableRoles.map((r) => (
          <option key={r} value={r}>
            {r === "super_admin" ? "super-admin" : r}
          </option>
        ))}
      </select>
    </li>
  )
}
